import { PortfolioItem } from "@/hooks/useProfile";
import { CaseStudyItem, CaseStudySection } from "@/types/case-study";

export type UnifiedPortfolioItemType = "shot" | "case_study";

export interface UnifiedPortfolioItem {
  id: string;
  type: UnifiedPortfolioItemType;
  title: string;
  description: string;
  cover_image: string;
  created_at: string;
  updated_at?: string;
  is_featured?: boolean;
  status?: "draft" | "published";
  order?: number;
  slug?: string;
  
  // Shot-specific properties (when type === "shot")
  image_url?: string;
  images?: Array<{
    id: string;
    url: string;
    order: number;
    caption?: string;
  }>;
  
  // Case study-specific properties (when type === "case_study")
  sections?: CaseStudySection[];
  seo_title?: string;
  seo_description?: string;
  og_image?: string;
}

/**
 * Converts a PortfolioItem to a UnifiedPortfolioItem
 */
export const portfolioItemToUnified = (item: PortfolioItem): UnifiedPortfolioItem => {
  return {
    id: item.id,
    type: "shot",
    title: item.title,
    description: item.description,
    cover_image: item.image_url || (item.images?.[0]?.url || ""),
    created_at: item.created_at,
    updated_at: item.updated_at,
    is_featured: item.is_featured,
    status: item.status,
    order: item.order,
    slug: item.slug,
    image_url: item.image_url,
    images: item.images,
  };
};

/**
 * Converts a CaseStudyItem to a UnifiedPortfolioItem
 */
export const caseStudyToUnified = (item: CaseStudyItem): UnifiedPortfolioItem => {
  return {
    id: item.id,
    type: "case_study",
    title: item.title,
    description: item.description,
    cover_image: item.cover_image,
    created_at: item.created_at,
    updated_at: item.updated_at,
    is_featured: item.is_featured,
    status: item.status,
    order: item.order,
    slug: item.slug,
    sections: item.sections,
    seo_title: item.seo_title,
    seo_description: item.seo_description,
    og_image: item.og_image,
  };
};

/**
 * Converts a UnifiedPortfolioItem back to a PortfolioItem
 */
export const unifiedToPortfolioItem = (item: UnifiedPortfolioItem): PortfolioItem => {
  if (item.type !== "shot") {
    throw new Error("Cannot convert case study to portfolio item");
  }
  
  return {
    id: item.id,
    title: item.title,
    description: item.description,
    image_url: item.image_url || "",
    images: (item.images || []).map(img => ({
      ...img,
      isLegacy: false
    })),
    created_at: item.created_at,
    updated_at: item.updated_at,
    is_featured: item.is_featured,
    status: item.status,
    order: item.order,
    slug: item.slug,
  };
};

/**
 * Converts a UnifiedPortfolioItem back to a CaseStudyItem
 */
export const unifiedToCaseStudy = (item: UnifiedPortfolioItem): CaseStudyItem => {
  if (item.type !== "case_study") {
    throw new Error("Cannot convert portfolio item to case study");
  }
  
  return {
    id: item.id,
    title: item.title,
    description: item.description,
    cover_image: item.cover_image,
    sections: item.sections || [],
    created_at: item.created_at,
    updated_at: item.updated_at,
    is_featured: item.is_featured,
    status: item.status,
    order: item.order,
    slug: item.slug,
    seo_title: item.seo_title,
    seo_description: item.seo_description,
    og_image: item.og_image,
  };
};
